import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from '@angular/material/sort';
import {ProviderService} from 'src/app/modules/ent/services/provider.service';
import {ProviderGroupService} from 'src/app/modules/ent/services/provider-group.service';
import {PagedRequestDTO} from 'src/app/modules/common/models/PagedRequestDTO';
import {TableControl} from 'src/app/modules/common/models/TableControl';
import {FormControl} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {ErrorDialogComponent} from '../../../../common/dialogs/error-dialog/error-dialog.component';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {ProviderDTO} from 'src/app/modules/ent/models/ProviderDTO';
import {ProviderGroupDTO} from 'src/app/modules/ent/models/ProviderGroupDTO';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Common, createFieldMap} from '../../../../common/util/Util';
import {HelpDTO} from '../../../../other/models/HelpDTO';
import {PolicyDTO} from '../../../models/PolicyDTO';
import {PolicyService} from '../../../services/policy.service';
import {PriceListDTO} from '../../../models/PriceListDTO';
import {PriceListService} from '../../../services/price-list.service';
import {DateUtil} from '../../../../common/util/DateUtil';
import {AddBankAccountDialogComponent} from '../../dialogs/add-bank-account-dialog/add-bank-account-dialog.component';
import {BankAccountDTO} from '../../../models/BankAccountDTO';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {detailsExpand} from '../../../../common';
import {DisplayIcon} from '../../../../common/components';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {TabSelectionService} from '../../../../common/services/tab-selection.service';
import {EnterpriseRoutesQueryParam as QueryParam} from '../../../../common/types/routes-query-param.enum';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {SharedService} from '../../../../common/services/shared-service';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {EntRequestBuilder} from '../../../services/ent-request-builder';
import {PolicyholderDTO} from '../../../models/PolicyholderDTO';

enum Tab {
  Provider,
  ProviderGroup,
  ProviderNetwork,
  ProviderPriceList,
  InsuredPerson,
  Policy,
  Applications,
  PolicyholderGroup,
  Brokers,
  Products,
  Benefits,
  Limits,
  ClinicalCoding,
}

const TabQueryParamByIndexNo = {
  [Tab.Provider]: QueryParam.Provider,
  [Tab.ProviderGroup]: QueryParam.ProviderGroup,
  [Tab.ProviderNetwork]: QueryParam.ProviderNetwork,
  [Tab.ProviderPriceList]: QueryParam.ProviderPriceList,
  [Tab.InsuredPerson]: QueryParam.Person,
  [Tab.Policy]: QueryParam.Policy,
  [Tab.Applications]: QueryParam.Applications,
  [Tab.PolicyholderGroup]: QueryParam.PolicyHolderGroups,
  [Tab.Brokers]: QueryParam.Brokers,
  [Tab.Products]: QueryParam.Products,
  [Tab.Benefits]: QueryParam.Benefits,
  [Tab.Limits]: QueryParam.Limits,
  [Tab.ClinicalCoding]: QueryParam.ClinicalCoding,
};

const ALL_GROUPS = 'All Types';
const ACTIVE_STATUS = 'Active';

@Component({
  selector: 'app-enterprise-management-view',
  templateUrl: './enterprise-management-view.component.html',
  styleUrls: [
    './enterprise-management-view.component.scss',
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class EnterpriseManagementViewComponent implements OnInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly DisplayIcon = DisplayIcon;

  // Form control
  providerFormControl: FormControl;
  providerGroupFormControl: FormControl;
  providerPriceListFormControl: FormControl;
  policyFormControl: FormControl;

  // Subscription
  providerNameSubscription: Subscription;
  providerGroupNameSubscription: Subscription;
  policySubscription: Subscription;
  providerPriceListNameSubscription: Subscription;

  providerNameTextChanged = new Subject<string>();
  providerGroupNameTextChanged = new Subject<string>();
  providerPriceListNameTextChanged = new Subject<string>();
  policyTextChanged = new Subject<string>();

  // MatTable columns
  providerDisplayedColumns: string[] = [
    'icons',
    'providerId',
    'practiceNumber',
    'name',
    'providerNetworkList',
    'businessLicenseNumber',
    'businessType',
    'entityStatus',
    'providerFacilityType',
    'startDate',
    'bankAccount'
  ];
  providerGroupDisplayedColumns: string[] = [
    'icons',
    'providerGroupId',
    'practiceNumber',
    'name',
    'businessLicenseNumber',
    'businessType',
    'entityStatus',
    'startDate'
  ];
  providerPriceListDisplayedColumns: string[] = [
    'icons',
    'providerPriceListId',
    'name',
    'providerPriceListType',
    'administrator'
  ];
  policyDisplayedColumns: string[] = [
    'icons',
    'name',
    'number',
    'startDate',
    'endDate',
    'policyholder',
    'payer',
    'administrator',
    'broker',
    'processingClass',
    'currency'
  ];

  // Data source
  providerDataSource: ProviderDTO[];
  providerGroupDataSource: ProviderGroupDTO[];
  providerPriceListDataSource: PriceListDTO[];
  policyDataSource: PolicyDTO[];

  // MatSort
  @ViewChild('providerSort', {static: false}) providerSort: MatSort; // TODO: Fix the sort ViewChild and set the initial sort header
  @ViewChild('providerGroupSort', {static: false}) providerGroupSort: MatSort;
  @ViewChild('providerPriceListSort', {static: false}) providerPriceListSort: MatSort;
  @ViewChild('policySort', {static: false}) policySort: MatSort;

  // MatPaginator Inputs
  @ViewChild('providerPaginator', {static: false})
  providerPaginator: MatPaginator;
  @ViewChild('providerGroupPaginator', {static: false})
  providerGroupPaginator: MatPaginator;
  @ViewChild('providerPriceListPaginator', {static: false})
  providerPriceListPaginator: MatPaginator;
  @ViewChild('policyPaginator', {static: false})
  policyPaginator: MatPaginator;

  // Table Control
  providerTableControl: TableControl;
  providerGroupTableControl: TableControl;
  providerPriceListTableControl: TableControl;
  policyTableControl: TableControl;

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  // Expanded Row
  providerExpandedRow: ProviderDTO | null;
  providerGroupExpandedRow: ProviderGroupDTO | null;
  providerPriceListExpandedRow: PriceListDTO | null;
  policyExpandedRow: PolicyholderDTO | null;

  // Other global variables
  activeTabName = 'Providers';
  activeTabIndex = 0;
  isConfirmed = false;
  dateFormat = DateUtil.dateFormat;
  dateTimeFormat = DateUtil.dateTimeFormat;
  helpInfo: HelpDTO[];
  hasPermissionsBasedOnRole: boolean;

  // Provider Variables
  providerGroup: string;
  providerGroups: NameValueDTO[];
  providerStatus: string;
  providerStatuses: NameValueDTO[];
  private currentTab: string;
  // Search inputs
  @ViewChild('providerSearch') private providerSearch: ElementRef;
  @ViewChild('providerGroupSearch') private providerGroupSearch: ElementRef;
  @ViewChild('providerPriceListSearch') private providerPriceListSearch: ElementRef;
  @ViewChild('policySearch') private policySearch: ElementRef;

  constructor(
    private snackBar: MatSnackBar,
    private providerService: ProviderService,
    private providerGroupService: ProviderGroupService,
    private providerPriceListService: PriceListService,
    private policyService: PolicyService,
    private router: Router,
    public dialog: MatDialog,
    private tabSelectionService: TabSelectionService,
    private lookupService: LookupService,
    private sharedService: SharedService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    this.providerFormControl = new FormControl('');
    this.providerGroupFormControl = new FormControl('');
    this.providerPriceListFormControl = new FormControl('');
    this.policyFormControl = new FormControl('');

    this.providerNameSubscription = this.providerNameTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.getListOfProviders(this.providerTableControl);
    });

    this.providerGroupNameSubscription = this.providerGroupNameTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.getListOfProviderGroups(this.providerGroupTableControl);
    });

    this.providerPriceListNameSubscription = this.providerPriceListNameTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.getListOfProviderPriceLists(this.providerPriceListTableControl);
    });
    this.policySubscription = this.policyTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.getListOfPolicies(this.policyTableControl);
    });

    this.providerGroups = this.updateLookupWithDefault([], ALL_GROUPS);
    this.providerGroup = ALL_GROUPS;
    this.providerStatuses = [];
    this.providerStatus = ACTIVE_STATUS;
    this.hasPermissionsBasedOnRole = this.userAuthorisationService.hasRoleEnterpriseManager();
  }

  ngOnInit(): void {
    this.providerTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'providerId',
      'desc',
      'name',
      ''
    );

    this.providerGroupTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'providerGroupId',
      'desc',
      'name',
      ''
    );

    this.providerPriceListTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'providerPriceListId',
      'desc',
      'name',
      ''
    );

    this.policyTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'policyId',
      'desc',
      'name',
      ''
    );

    this.getListOfLookupValues(this.lookupValue.ProviderGroup);
    this.getListOfLookupValues(this.lookupValue.EntityStatus);

    this.tabSelectionService.currentTab$AsObservable
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((currentTab: string) => {
      this.currentTab = currentTab;
      this.selectTab(currentTab);
    });

    this.getListOfProviderGroups(this.providerGroupTableControl);
    this.getListOfProviderPriceLists(this.providerPriceListTableControl);
    this.getListOfPolicies(this.policyTableControl);
  }

  onInputChangedSearchProviders($event) {
    this.providerTableControl.filterField = 'name';
    this.providerTableControl.filterValue = $event.target.value;
    this.providerNameTextChanged.next($event.target.value);
  }

  onInputChangedSearchProviderGroups($event) {
    this.providerGroupTableControl.filterField = 'name';
    this.providerGroupTableControl.filterValue = $event.target.value;
    this.providerGroupNameTextChanged.next($event.target.value);
  }

  onInputChangedSearchProviderPriceLists($event) {
    this.providerPriceListTableControl.filterField = 'name';
    this.providerPriceListTableControl.filterValue = $event.target.value;
    this.providerPriceListNameTextChanged.next($event.target.value);
  }

  onInputChangedSearchPolicy($event) {
    this.policyTableControl.filterField = 'number';
    this.policyTableControl.filterValue = $event.target.value;
    this.policyTextChanged.next($event.target.value);
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo ? helpInfo.inputInfo : '';
    }
    return message;
  }

  selectTab(tabName: string) {
    if (tabName === QueryParam.Provider) {
      this.activeTabIndex = Tab.Provider;
      this.activeTabName = 'Providers';
      setTimeout(() => this.providerSearch.nativeElement.focus(), 0);
    }
    else if (tabName === QueryParam.ProviderGroup) {
      this.activeTabIndex = Tab.ProviderGroup;
      this.activeTabName = 'Provider Groups';
      setTimeout(() => this.providerGroupSearch.nativeElement.focus(), 0);
    }
    else if (tabName === QueryParam.ProviderNetwork) {
      this.activeTabIndex = Tab.ProviderNetwork;
      this.activeTabName = 'Provider Network';
    }
    else if (tabName === QueryParam.ProviderPriceList) {
      this.activeTabIndex = Tab.ProviderPriceList;
      this.activeTabName = 'Provider Price Lists';
      setTimeout(() => this.providerPriceListSearch.nativeElement.focus(), 0);
    }
    else if (tabName === QueryParam.Person) {
      this.activeTabIndex = Tab.InsuredPerson;
      this.activeTabName = 'Persons';
    }
    else if (tabName === QueryParam.PolicyHolderGroups) {
      this.activeTabIndex = Tab.PolicyholderGroup;
      this.activeTabName = 'Policyholder Groups';
    }
    else if (tabName === QueryParam.Policy) {
      this.activeTabIndex = Tab.Policy;
      this.activeTabName = 'Policies';
      setTimeout(() => this.policySearch.nativeElement.focus(), 0);
    }
    else if (tabName === QueryParam.Applications) {
      this.activeTabIndex = Tab.Applications;
      this.activeTabName = 'Applications';
      // Todo: Once Filter is implemented focus Input field
    }
    else if (tabName === QueryParam.Brokers) {
      this.activeTabIndex = Tab.Brokers;
      this.activeTabName = 'Brokers';
      // Todo: Once Filter is implemented focus Input field
    }
    else if (tabName === QueryParam.Products) {
      this.activeTabIndex = Tab.Products;
      this.activeTabName = 'Products';
    }
    else if (tabName === QueryParam.Benefits) {
      this.activeTabIndex = Tab.Benefits;
      this.activeTabName = 'Benefits';
    }
    else if (tabName === QueryParam.ClinicalCoding) {
      this.activeTabIndex = Tab.ClinicalCoding;
      this.activeTabName = 'Clinical Coding';
    }
    else if (tabName === QueryParam.Limits) {
      this.activeTabIndex = Tab.Limits;
      this.activeTabName = 'Limits';
    }
  }

  getListOfProviders(tableControl: TableControl): void {
    const basicPagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO('TblProvider', tableControl);
    const pagedRequestDTO = this.addFieldMapForPagedRequestDTO(
      basicPagedRequestDTO, ['name', 'businessLicenseNumber', 'practiceNumber'], tableControl.filterValue
    );

    if (this.providerGroup != ALL_GROUPS && this.providerGroups != null && this.providerGroups.length > 0) {
      const pagedFilterProviderGroupDTO = new PagedFilterDTO();
      pagedFilterProviderGroupDTO.name = 'providerGroupByProviderGroupId.name';
      pagedFilterProviderGroupDTO.value = this.providerGroups.find((group) => group.name === this.providerGroup)?.name;
      basicPagedRequestDTO.fieldMap[`providerGroupByProviderGroupId`] = pagedFilterProviderGroupDTO;
    }

    if (this.providerStatus) {
      const pagedFilterEntityStatusDTO = new PagedFilterDTO();
      pagedFilterEntityStatusDTO.name = 'entityStatusByEntityStatusId.name';
      pagedFilterEntityStatusDTO.value = this.providerStatus;
      basicPagedRequestDTO.fieldMap[`entityStatusByEntityStatusId`] = pagedFilterEntityStatusDTO;
    }

    this.providerService.getListOfProviders(pagedRequestDTO).subscribe({
      next: (response) => {
        this.providerDataSource = response.resultList;
        this.providerTableControl.length = response.totalRecords;
      },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    });
  }

  getListOfProviderGroups(tableControl: TableControl): void {
    const basicPagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO(EntityTable.TblProviderGroup, tableControl);
    const pagedRequestDTO = this.addFieldMapForPagedRequestDTO(
      basicPagedRequestDTO, ['name', 'practiceNumber'], tableControl.filterValue
    );

    this.providerGroupService.getListOfProviderGroups(pagedRequestDTO).subscribe({
      next: (response) => {
        this.providerGroupDataSource = response.resultList;
        this.providerGroupTableControl.length = response.totalRecords;
      },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    });
  }

  addFieldMapForPagedRequestDTO(pagedRequestDTO: PagedRequestDTO, fieldMapItems: string[], filterValue: string) {
    for (let i = 0; i < fieldMapItems.length; i++) {
      const pagedFilterDTO = new PagedFilterDTONew();
      pagedFilterDTO.name = fieldMapItems[i];
      pagedFilterDTO.value = filterValue.trim();
      pagedRequestDTO.fieldMap[fieldMapItems[i]] = pagedFilterDTO;

      if (i !== 0) {
        pagedFilterDTO.logicGate = 'OR';
      }

      if (fieldMapItems.length > 1) {
        const isFirst = i === 0;
        const isLast = i === fieldMapItems.length - 1;
        pagedFilterDTO.bracket = isFirst ? '(' : (isLast ? ')' : '');
      }
    }

    return pagedRequestDTO;
  }

  getListOfProviderPriceLists(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO('TblProviderPriceList', tableControl);

    pagedRequestDTO.fieldMap = createFieldMap(
      tableControl.filterField,
      tableControl.filterValue
    );

    this.providerPriceListService.getListOfPriceLists(pagedRequestDTO).subscribe({
      next: (response) => {
        this.providerPriceListDataSource = response.resultList;
        this.providerPriceListTableControl.length = response.totalRecords;
      },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    });
  }

  getListOfPolicies(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO(EntityTable.TblPolicy, tableControl);

    pagedRequestDTO.fieldMap = createFieldMap(
      tableControl.filterField,
      tableControl.filterValue
    );

    this.policyService.getListOfPolicies(pagedRequestDTO).subscribe({
      next: (response) => {
        this.policyDataSource = (response.resultList as PolicyDTO[]);
        this.policyTableControl.length = response.totalRecords;
      },
      error: (errorResponse) => {
        const error = errorResponse.error;
        this.openErrorDialog(error.businessMessage);
      }
    });
  }

  handleError(errorMessage: string) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  pageChanged($event: PageEvent) {
    if (this.activeTabName === 'Providers') {
      this.providerTableControl.pageNumber = $event.pageIndex;
      this.providerTableControl.pageSize = $event.pageSize;
      this.getListOfProviders(this.providerTableControl);
    }
    else if (this.activeTabName === 'Provider Groups') {
      this.providerGroupTableControl.pageNumber = $event.pageIndex;
      this.providerGroupTableControl.pageSize = $event.pageSize;
      this.getListOfProviderGroups(this.providerGroupTableControl);
    }
    else if (this.activeTabName === 'Provider Price Lists') {
      this.providerPriceListTableControl.pageNumber = $event.pageIndex;
      this.providerPriceListTableControl.pageSize = $event.pageSize;
      this.getListOfProviderPriceLists(this.providerPriceListTableControl);
    }
    else if (this.activeTabName === 'Policies') {
      this.policyTableControl.pageNumber = $event.pageIndex;
      this.policyTableControl.pageSize = $event.pageSize;
      this.getListOfPolicies(this.policyTableControl);
    }
  }

  sortData($event: Sort) {
    if (this.activeTabName === 'Providers') {
      if (this.providerPaginator != null) {
        this.providerPaginator.firstPage();
      }
      this.providerTableControl.pageNumber = 0;
      this.providerTableControl.sortField = $event.active;
      this.providerTableControl.sortDirection = $event.direction;
      this.getListOfProviders(this.providerTableControl);
    }
    else if (this.activeTabName === 'Provider Groups') {
      if (this.providerGroupPaginator != null) {
        this.providerGroupPaginator.firstPage();
      }
      this.providerGroupTableControl.pageNumber = 0;
      this.providerGroupTableControl.sortField = $event.active;
      this.providerGroupTableControl.sortDirection = $event.direction;
      this.getListOfProviderGroups(this.providerGroupTableControl);
    }
    else if (this.activeTabName === 'Provider Price Lists') {
      if (this.providerPriceListPaginator != null) {
        this.providerPriceListPaginator.firstPage();
      }
      this.providerPriceListTableControl.pageNumber = 0;
      this.providerPriceListTableControl.sortField = $event.active;
      this.providerPriceListTableControl.sortDirection = $event.direction;
      this.getListOfProviderPriceLists(this.providerPriceListTableControl);
    }
    else if (this.activeTabName === 'Policies') {
      if (this.policyPaginator != null) {
        this.policyPaginator.firstPage();
      }
      this.policyTableControl.pageNumber = 0;
      this.policyTableControl.sortField = $event.active;
      this.policyTableControl.sortDirection = $event.direction;
      this.getListOfPolicies(this.policyTableControl);
    }
  }

  deleteEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.activeTabName === 'Providers') {
          if (!this.hasPermissionsBasedOnRole) {
            return;
          }

          this.providerService.deleteProvider(id).subscribe({
            next: () => {
              this.openSnackBar(`Provider ${id} deleted`, 'Dismiss');
              this.getListOfProviders(this.providerTableControl);
            },
            error: (errorResponse) => {
              const error = errorResponse.error;
              this.openErrorDialog(error.businessMessage);
            }
          });
        }
        else if (this.activeTabName === 'Provider Groups') {
          if (!this.hasPermissionsBasedOnRole) {
            return;
          }

          this.providerGroupService.deleteProviderGroup(id).subscribe({
            next: () => {
              this.openSnackBar(`Provider Group ${id} deleted`, 'Dismiss');
              this.getListOfProviderGroups(this.providerGroupTableControl);

            },
            error: (errorResponse) => {
              const error = errorResponse.error;
              this.openErrorDialog(error.businessMessage);
            }
          });
        }
        else if (this.activeTabName === 'Provider Price Lists') {
          this.providerPriceListService.deletePriceList(id).subscribe({
            next: () => {
              this.openSnackBar(`Provider Price List ${id} deleted`, 'Dismiss');
              this.getListOfProviderPriceLists(this.providerPriceListTableControl);

            },
            error: (errorResponse) => {
              const error = errorResponse.error;
              this.openErrorDialog(error.businessMessage);
            }
          });
        }
        else if (this.activeTabName === 'Policies') {
          this.policyService.deletePolicy(id).subscribe({
            next: () => {
              this.openSnackBar(`Policy  ${id} deleted`, 'Dismiss');
              this.getListOfPolicies(this.policyTableControl);
            },
            error: (errorResponse) => {
              const error = errorResponse.error;
              this.openErrorDialog(error.businessMessage);
            }
          });
        }
      }
    });
  }

  editEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    if (this.activeTabName === 'Providers') {
      this.router.navigate(['/enterprise-management/edit-provider/' + id]);
    }
    else if (this.activeTabName === 'Provider Groups') {
      this.router.navigate(['/enterprise-management/edit-provider-group/' + id]);
    }
    else if (this.activeTabName === 'Provider Price Lists') {
      this.router.navigate(['/enterprise-management/edit-provider-price-list/' + id]);
    }
    else if (this.activeTabName === 'Policyholder Groups') {
      this.router.navigate(['/enterprise-management/edit-policyholder-group/' + id]);
    }
    else if (this.activeTabName === 'Policies') {
      this.router.navigate(['/enterprise-management/edit-policy/' + id]);
    }
  }

  onTabSelectionChanged($event: MatTabChangeEvent) {
    this.router.navigate(['/enterprise-management'], {
      queryParams: {tab: TabQueryParamByIndexNo[$event.index]},
    });
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  openErrorDialog(errorMessage: string) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  ngOnDestroy(): void {
    this.providerNameSubscription.unsubscribe();
    this.providerGroupNameSubscription.unsubscribe();
    this.policySubscription.unsubscribe();
    this.providerPriceListNameSubscription.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  openProviderBankAccountDialog(event: MouseEvent, providerId: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(AddBankAccountDialogComponent, {
      width: '40vw',
      height: '80vh',
      data: {entityId: providerId, entityType: 'Provider'}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getListOfProviders(this.providerTableControl);
      }
    });

  }

  getProviderBankAccountDisplayString(bankAccount: BankAccountDTO): string {
    return bankAccount != null ? 'Edit Account' : 'Add Account';
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe({
      next: (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.ProviderGroup) {
          this.handleLookupValuesResponses(response);
          this.providerGroup = this.providerGroups[0].name;
        }
        else if (entity === this.lookupValue.EntityStatus) {
          this.providerStatuses = response || [];
          this.providerStatus = this.providerStatuses.find((status) => status.name === ACTIVE_STATUS)?.name ?? this.providerStatuses[0]?.name ?? ACTIVE_STATUS;
          if (this.providerTableControl) {
            this.getListOfProviders(this.providerTableControl);
          }
        }
      },
      error: (errorResponse) => {
        if (entity === this.lookupValue.EntityStatus && this.providerTableControl) {
          this.getListOfProviders(this.providerTableControl);
        }
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    });
  }

  private handleLookupValuesResponses(responses: NameValueDTO[]): void {
    this.providerGroups = this.updateLookupWithDefault(responses, ALL_GROUPS);
  }

  private updateLookupWithDefault(response: NameValueDTO[] | null, defaultName: string): NameValueDTO[] {
    const processedArray: NameValueDTO[] = [...(response || [])];
    processedArray.unshift({value: 0, name: defaultName});
    return processedArray;
  }

  onProviderGroupSelectionChanged(value: string) {
    this.providerGroup = value;
    this.getListOfProviders(this.providerTableControl);
  }

  onProviderStatusSelectionChanged(value: string) {
    this.providerStatus = value;
    this.getListOfProviders(this.providerTableControl);
  }

}
